import Icon from "@/components/library/Icon";
import AuthCard from "@/components/screens/Login/Components/AuthCard";
import AuthHeader from "@/components/screens/Login/Components/AuthHeader";
import AuthHero from "@/components/screens/Login/Components/AuthHero";
import ForgotPasswordAlert from "@/components/screens/Login/Components/ForgotPasswordAlert";
import LoginFooter from "@/components/screens/Login/Components/LoginFooter";
import LoginForm from "@/components/screens/Login/Components/LoginForm";
import QRScanner from "@/components/screens/Login/Components/QRScanner";
import type { ILoginViewModel } from "@/components/screens/Login/Types/LoginTypes";
import { faArrowRight, faHouse } from "@/icons/solid-icons";
import { cn } from "@/utils/css";

const authLinkClassName =
  "ml-1 font-bold text-white border-b-[1.5px] border-dines-green pb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-dines-green rounded-sm";

const LoginScreen = ({ vm }: { vm: ILoginViewModel }) => (
  <div className="flex min-h-screen flex-col overflow-x-hidden bg-sidebar-gradient px-6 py-8 text-white sm:px-12 sm:py-10">
    <AuthHeader
      logoAlt={vm.labels.logoAlt}
      logoAriaLabel={vm.labels.logoAriaLabel}
      logoHref={vm.labels.logoHref}
    />

    <main className="flex flex-1 items-center justify-center py-10">
      <AuthCard>
        {vm.isAutomaticallyLoggingIn ? (
          <p className="text-white/70">Signing in…</p>
        ) : vm.forgotPasswordAlert.showAlert ? (
          <ForgotPasswordAlert vm={vm.forgotPasswordAlertViewModel} />
        ) : (
          <>
            <AuthHero
              eyebrow={vm.labels.eyebrow}
              headingLineOne={vm.labels.headingLineOne}
              headingLineTwo={vm.labels.headingLineTwo}
              subtitle={vm.labels.subtitle}
            />

            <div className="mt-8">
              <LoginForm vm={vm.formVm} />
            </div>

            {vm.otpEntry.isVisible && (
              <p className="mt-6 text-center text-sm text-white/60">
                {vm.otpEntry.leadingText}
                <button
                  type="button"
                  onClick={vm.otpEntry.onClick}
                  className={authLinkClassName}
                >
                  {vm.otpEntry.linkText}
                </button>
              </p>
            )}

            {vm.partnerLogin.isVisible && (
              <a
                id="partner-login-redirect"
                href={vm.partnerLogin.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={vm.partnerLogin.labels.ariaLabel}
                className={cn(
                  "group mt-4 flex items-center gap-4 rounded-2xl px-4 py-3.5",
                  "bg-white/[0.04] hover:bg-white/[0.07]",
                  "border border-white/[0.06] hover:border-dines-green/40",
                  "transition-colors duration-200",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-dines-green",
                )}
              >
                <span
                  className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                    "bg-dines-green/15 text-dines-green",
                  )}
                >
                  <Icon icon={faHouse} className="h-4 w-4" />
                </span>
                <span className="flex flex-col text-left leading-tight">
                  <span className="font-bold text-sm text-white">
                    {vm.partnerLogin.labels.title}
                  </span>
                  <span className="text-white/60 text-xs">
                    {vm.partnerLogin.labels.subtitle}
                  </span>
                </span>
                <span
                  className={cn(
                    "ml-auto text-dines-green transition-transform duration-200",
                    "group-hover:translate-x-0.5",
                  )}
                >
                  <Icon icon={faArrowRight} className="h-4 w-4" />
                </span>
              </a>
            )}
          </>
        )}
      </AuthCard>
    </main>

    <LoginFooter vm={vm.footerViewModel} />

    {vm.isScanning && <QRScanner vm={vm.qrScanner} />}
  </div>
);

export default LoginScreen;
